import clsx from 'clsx';
import type { HTMLAttributes, JSX, ReactNode } from 'react';

type SectionTone = 'base' | 'muted' | 'contrast';

type SectionSpacing = 'none' | 'sm' | 'md' | 'lg';

export interface PublicPageSectionProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  tone?: SectionTone;
  spacing?: SectionSpacing;
  container?: boolean;
  containerClassName?: string;
  stacked?: boolean;
}

const TONE_CLASS: Record<SectionTone, string> = {
  base: 'bg-surface-light',
  muted: 'bg-surface-cream',
  contrast: 'bg-brand-primary text-white',
};

const SPACING_CLASS: Record<SectionSpacing, string> = {
  none: 'py-0',
  sm: 'py-10 md:py-12 lg:py-14',
  md: 'py-12 md:py-16 lg:py-20',
  lg: 'py-16 md:py-20 lg:py-24',
};

export function PublicPageSection({
  children,
  tone = 'base',
  spacing = 'md',
  container = true,
  stacked = true,
  containerClassName,
  className,
  ...rest
}: PublicPageSectionProps): JSX.Element {
  const sectionClassName = clsx(TONE_CLASS[tone], SPACING_CLASS[spacing], className);

  if (!container) {
    return (
      <section {...rest} className={sectionClassName}>
        {children}
      </section>
    );
  }

  return (
    <section {...rest} className={sectionClassName}>
      <div
        className={clsx(
          'container',
          stacked ? 'flex flex-col gap-6 md:gap-8' : null,
          containerClassName,
        )}
      >
        {children}
      </div>
    </section>
  );
}
